

import { useState, useEffect } from "react";
import {
  Button,
  Table,
  Spin,
  Alert,
  message,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Card,
} from "antd";
import {
  FileExcelOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { getGramSabhaReportAll } from "../api/User";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FilePdfOutlined } from "@ant-design/icons";

const { Option } = Select;

const GramSabhaReport = ({ dateFilter }) => {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Filter states
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedGP, setSelectedGP] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gramPanchayats, setGramPanchayats] = useState([]);

  const columns = [
    {
      title: "Sr. NO.",
      key: "sr_no",
      dataIndex: "sr_no",
      width: 60,
      fixed: "left",
      align: "center",
      render: (_, __, index) =>
        (currentPage - 1) * pageSize + index + 1,
       sorter: (a, b, sortOrder) => {
      // Sort by the displayed serial number
      const indexA = (currentPage - 1) * pageSize + data.indexOf(a) + 1;
      const indexB = (currentPage - 1) * pageSize + data.indexOf(b) + 1;
      return sortOrder === 'ascend' ? indexA - indexB : indexB - indexA;
    },
    },

    {
      title: "District Name",
      dataIndex: "district_name",
      key: "district_name",
      width: 120,
      align: "center",
      sorter: (a, b) => a.district_name?.localeCompare(b.district_name),
      ellipsis: true,
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      key: "block_name",
      width: 120,
      align: "center",
      sorter: (a, b) => a.block_name?.localeCompare(b.block_name),
      ellipsis: true,
    },
    {
      title: "Gram Panchayat Name",
      dataIndex: "gp_name",
      key: "gp_name",
      width: 140,
      align: "center",
      sorter: (a, b) => a.gp_name?.localeCompare(b.gp_name),
      ellipsis: true,
    },
    {
      title: "Village Name",
      dataIndex: "village_names",
      key: "village_names",
      width: 180,
      align: "center",
      sorter: (a, b) => a.village_names?.localeCompare(b.village_names),
      ellipsis: true,
    },
    {
      title: "Sabha Date",
      dataIndex: "sabha_date",
      key: "sabha_date",
      width: 110,
      align: "center",
      sorter: (a, b) => {
      // Parse dates for proper sorting
      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === "N/A") return new Date(0);
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        return new Date(dateStr) || new Date(0);
      };
      const dateA = parseDate(a.sabha_date);
      const dateB = parseDate(b.sabha_date);
      return dateA.getTime() - dateB.getTime();
    },
      
      render: (sabha_date) => (sabha_date ? sabha_date : "N/A"),
    },
    {
      title: "Meeting Scheduled?",
      dataIndex: "is_scheduled",
      key: "is_scheduled",
      width: 110,
      align: "center",
      sorter: (a, b) => (a.is_scheduled || '').localeCompare(b.is_scheduled || ''),
      render: (val) => (
        <Tag color={val === "Y" ? "green" : "red"} style={{ fontSize: "10px" }}>
          {val === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Gram Sabha Conducted?",
      dataIndex: "is_conducted",
      key: "is_conducted",
      width: 120,
      align: "center",
      sorter: (a, b) => (a.is_conducted || '').localeCompare(b.is_conducted || ''),
      render: (val) => (
        <Tag
          color={val === "Y" ? "green" : "orange"}
          style={{ fontSize: "10px" }}
        >
          {val === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    fetchReport();
  }, [dateFilter]);

  useEffect(() => {
    if (originalData.length > 0) {
      extractFilterOptions(originalData);
    }
  }, [originalData]);

  const extractFilterOptions = (data) => {
    // Extract unique districts
    const uniqueDistricts = [
      ...new Set(data.map((item) => item.district_name).filter(Boolean)),
    ];
    setDistricts(
      uniqueDistricts.map((district) => ({ label: district, value: district }))
    );

    // Extract blocks based on selected district
    let filteredData = data;
    if (selectedDistrict) {
      filteredData = data.filter(
        (item) => item.district_name === selectedDistrict
      );
    }
    const uniqueBlocks = [
      ...new Set(filteredData.map((item) => item.block_name).filter(Boolean)),
    ];
    setBlocks(uniqueBlocks.map((block) => ({ label: block, value: block })));

    // Extract GPs based on selected district and block
    if (selectedBlock) {
      filteredData = filteredData.filter(
        (item) => item.block_name === selectedBlock
      );
    }
    const uniqueGPs = [
      ...new Set(filteredData.map((item) => item.gp_name).filter(Boolean)),
    ];
    setGramPanchayats(uniqueGPs.map((gp) => ({ label: gp, value: gp })));
  };

  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedBlock(null);
    setSelectedGP(null);
    applyFilters(value, null, null, searchDate);
  };

  const handleBlockChange = (value) => {
    setSelectedBlock(value);
    setSelectedGP(null);
    applyFilters(selectedDistrict, value, null, searchDate);
  };

  const handleGPChange = (value) => {
    setSelectedGP(value);
    applyFilters(selectedDistrict, selectedBlock, value, searchDate);
  };

  const applyFilters = (district, block, gp, date) => {
    let filtered = [...originalData];

    // Apply district filter
    if (district) {
      filtered = filtered.filter((item) => item.district_name === district);
    }

    // Apply block filter
    if (block) {
      filtered = filtered.filter((item) => item.block_name === block);
    }

    // Apply GP filter
    if (gp) {
      filtered = filtered.filter((item) => item.gp_name === gp);
    }

    // Apply date filter
    if (date && date.trim()) {
      filtered = filtered.filter((item) => {
        if (!item.original_sabha_date) return false;
        const itemFormattedDate = formatDate(item.original_sabha_date);
        return itemFormattedDate === date;
      });
    }


    setAllData(filtered);
    setData(filtered.slice(0, pageSize));
    setTotal(filtered.length);
    setCurrentPage(1);

    // Update filter options
    if (originalData.length > 0) {
      extractFilterOptions(originalData);
    }
  };

  const handleExportPDF = async () => {
    try {
      const dataToExport = allData.length > 0 ? allData : data;

      if (!dataToExport.length) {
        message.warning("No data to export");
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(14);
      doc.text("Gram Sabha Report", pageWidth / 2, 12, { align: "center" });

      // Date and filter info
      doc.setFontSize(9);
      let filterText = `Generated on: ${new Date().toLocaleString()}`;

      const filters = [];
      if (selectedDistrict) filters.push(`District: ${selectedDistrict}`);
      if (selectedBlock) filters.push(`Block: ${selectedBlock}`);
      if (selectedGP) filters.push(`GP: ${selectedGP}`);
      if (searchDate) filters.push(`Date: ${searchDate}`);

      if (filters.length > 0) {
        filterText += ` | Filters: ${filters.join(", ")}`;
      }

      doc.text(filterText, pageWidth / 2, 18, { align: "center" });

      // Table Header
      const headers = columns.map((col) => col.title);

      // Table Body
      const body = dataToExport.map((row, index) =>
        columns.map((col) => {
          if (col.dataIndex === "sr_no") return index + 1;
          return row[col.dataIndex] ?? "";
        })
      );

      autoTable(doc, {
        startY: 24,
        head: [headers],
        body,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 119, 255],
          textColor: 255,
          fontStyle: "bold",
        },
        margin: { left: 5, right: 5 },
        tableWidth: "auto",
      });

      let fileName = `Gram_Sabha_Report_${Date.now()}.pdf`;
      if (selectedDistrict || selectedBlock || selectedGP || searchDate) {
        const filterParts = [];
        if (selectedDistrict)
          filterParts.push(selectedDistrict.substring(0, 3));
        if (selectedBlock) filterParts.push(selectedBlock.substring(0, 3));
        if (selectedGP) filterParts.push(selectedGP.substring(0, 3));
        if (searchDate) filterParts.push(searchDate.replace(/-/g, ""));
        fileName = `Gram_Sabha_Report_${filterParts.join("_")}.pdf`;
      }

      doc.save(fileName);
      message.success("PDF exported successfully");
    } catch (error) {
      console.error("PDF Export Error:", error);
      message.error("Failed to export PDF");
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";

    try {
      // Try parsing as ISO string
      const d = new Date(date);
      if (isNaN(d.getTime())) {
        // Try parsing as DD-MM-YYYY
        const parts = date.split("-");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const newDate = new Date(year, month, day);
          if (!isNaN(newDate.getTime())) {
            const formattedDay = String(newDate.getDate()).padStart(2, "0");
            const formattedMonth = String(newDate.getMonth() + 1).padStart(
              2,
              "0"
            );
            const formattedYear = newDate.getFullYear();
            return `${formattedDay}-${formattedMonth}-${formattedYear}`;
          }
        }
        return "N/A";
      }

      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (err) {
      return "N/A";
    }
  };

  const validateDate = (dateString) => {
    // Check DD-MM-YYYY format
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!regex.test(dateString)) {
      return {
        isValid: false,
        error: "Please enter date in DD-MM-YYYY format",
      };
    }

    const parts = dateString.split("-");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Basic validation
    if (day < 1 || day > 31) {
      return { isValid: false, error: "Day must be between 01 and 31" };
    }
    if (month < 1 || month > 12) {
      return { isValid: false, error: "Month must be between 01 and 12" };
    }
    if (year < 1900 || year > 2100) {
      return { isValid: false, error: "Year must be between 1900 and 2100" };
    }

    // Validate actual date
    const date = new Date(year, month - 1, day);
    if (
      date.getDate() !== day ||
      date.getMonth() + 1 !== month ||
      date.getFullYear() !== year
    ) {
      return { isValid: false, error: "Invalid date" };
    }

    return { isValid: true };
  };

  const fetchReport = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem("token");

      // Fetch all data
      const result = await getGramSabhaReportAll(token, 1, 10000000, dateFilter);

      if (!result?.success) {
        setError(result.message || "Failed to load report data");
        return;
      }

      const formatted = result.data.map((d, index) => ({
        key: index + 1,

        district_name: d.district_name,
        block_name: d.block_name,
        gp_name: d.panchayat_name,
        village_names: Array.isArray(d.villages)
          ? d.villages.join(", ")
          : d.villages,
        is_scheduled:
          d.is_special_gram_sabha_meeting_scheduled === "yes" ? "Y" : "N",
        is_conducted: d.is_special_gram_sabha_conducted === "yes" ? "Y" : "N",
        sabha_date: formatDate(d.sabha_date),
        original_sabha_date: d.sabha_date,
      }));

      setOriginalData(formatted);
      setAllData(formatted);
      setData(formatted.slice((page - 1) * limit, page * limit));
      setTotal(formatted.length);

      // Extract filter options
      extractFilterOptions(formatted);
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDateSearch = () => {
    if (searchDate.trim()) {
      const validation = validateDate(searchDate.trim());
      if (!validation.isValid) {
        message.error(validation.error);
        return;
      }
      applyFilters(
        selectedDistrict,
        selectedBlock,
        selectedGP,
        searchDate.trim()
      );
    } else {
      applyFilters(selectedDistrict, selectedBlock, selectedGP, "");
    }
  };

  const handleClearAllFilters = () => {
    setSelectedDistrict(null);
    setSelectedBlock(null);
    setSelectedGP(null);
    setSearchDate("");
    setCurrentPage(1);

    if (originalData.length > 0) {
      setAllData(originalData);
      setData(originalData.slice(0, pageSize));
      setTotal(originalData.length);
      extractFilterOptions(originalData);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Remove any non-digit characters
    const cleanValue = value.replace(/[^\d]/g, "");

    // Format as DD-MM-YYYY
    let formatted = cleanValue;
    if (cleanValue.length > 4) {
      formatted = `${cleanValue.slice(0, 2)}-${cleanValue.slice(
        2,
        4
      )}-${cleanValue.slice(4, 8)}`;
    } else if (cleanValue.length > 2) {
      formatted = `${cleanValue.slice(0, 2)}-${cleanValue.slice(2)}`;
    }

    setSearchDate(formatted);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleDateSearch();
    }
  };

  const handleTableChange = (pagination) => {
    const newPage = pagination.current;
    const newPageSize = pagination.pageSize;

    setCurrentPage(newPage);
    setPageSize(newPageSize);

    const startIndex = (newPage - 1) * newPageSize;
    const endIndex = startIndex + newPageSize;
    setData(allData.slice(startIndex, endIndex));
  };

 const handleExportExcel = () => {
  const dataToExport = allData.length > 0 ? allData : data;

  if (!dataToExport.length) {
    message.warning("No data to export");
    return;
  }

  try {
    // Prepare headers (include SR. NO.)
    const headers = columns.map((c) => `"${c.title}"`).join(",");

    // Prepare rows
    const rows = dataToExport.map((row, index) =>
      columns
        .map((c) => {
          if (c.title === "Sr. NO.") {
            return `"${index + 1}"`; // 👈 sequential SR. NO.
          }
          return `"${(row[c.dataIndex] ?? "").toString()}"`;
        })
        .join(",")
    );

    const csv = [headers, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);

    let fileName = `Gram_Sabha_Report_${
      new Date().toISOString().split("T")[0]
    }.csv`;

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("Report exported successfully!");
  } catch (err) {
    console.error(err);
    message.error("Export failed");
  }
};

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedDistrict) count++;
    if (selectedBlock) count++;
    if (selectedGP) count++;
    if (searchDate.trim()) count++;
    return count;
  };

  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">Gram Sabha Report</h2>
            <p className="report-subtitle">
              Total Records: <strong>{total}</strong>
            </p>
          </div>

          <div className="report-actions">
            <Button
              onClick={handleExportPDF}
              icon={<FilePdfOutlined />}
              disabled={!data.length}
              style={{ marginRight: 8 }}
            >
              Export PDF
            </Button>

            <Button
              type="primary"
              onClick={handleExportExcel}
              icon={<FileExcelOutlined />}
             
              disabled={!data.length}
              style={{ marginRight: 8 }}
            >
              Export Excel
            </Button>

            <Button
              onClick={() => {
                handleClearAllFilters();
                fetchReport();
              }}
              icon={<ReloadOutlined />}
             
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <Card
          size="small"
          title={
            <span>
              <FilterOutlined style={{ marginRight: 8 }} />
              Filters
            </span>
          }
          style={{ marginBottom: 16 }}
          extra={
            getActiveFilterCount() > 0 && (
              <Button size="small" onClick={handleClearAllFilters}>
                Clear All Filters
              </Button>
            )
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Select District"
                style={{ width: "100%" }}
                value={selectedDistrict}
                onChange={handleDistrictChange}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={districts}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Select Block"
                style={{ width: "100%" }}
                value={selectedBlock}
                onChange={handleBlockChange}
                disabled={!selectedDistrict}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={blocks}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Select Gram Panchayat"
                style={{ width: "100%" }}
                value={selectedGP}
                onChange={handleGPChange}
                disabled={!selectedBlock}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={gramPanchayats}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input.Group compact style={{ display: "flex" }}>
                <Input
                  type="text"
                  placeholder="Date (DD-MM-YYYY)"
                  value={searchDate}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  maxLength={10}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleDateSearch}
                  disabled={!searchDate.trim()}
                >
                  Search
                </Button>
              </Input.Group>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            type="error"
            message="Error"
            description={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="Loading Report..." />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={handleTableChange}
            bordered
            scroll={{ x: 900, y: 420 }}
            size="small"
          />
        )}
      </div>
    </div>
  );
};

export default GramSabhaReport;
